import { useState, useEffect } from "react";
import { GitCompare, AlertTriangle, AlertCircle, CheckCircle, RefreshCw } from "lucide-react";
import axios, { API } from "@/utils/api";
import { useT, useDeptName } from "@/contexts/LanguageContext";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";

const ResidentialComparisonView = ({ clientId }) => {
  const t = useT();
  const dn = useDeptName();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/clients/${clientId}/residential/comparison/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      console.error("Error fetching residential comparison:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboard(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;

  const deptData = dashboard?.departments || [];
  const discrepancies = dashboard?.discrepancies || [];
  const summ = dashboard?.summary || {};

  return (
    <div data-testid="residential-comparison-dashboard">
      <div className="page-header flex-between">
        <div>
          <h2 className="page-title">{t("residential_audits")} — {t("comparison_title")}</h2>
          <p className="page-subtitle">{t("comparison_subtitle")}</p>
        </div>
        <button className="btn btn-primary" onClick={fetchDashboard}>
          <RefreshCw size={16} /> {t("refresh")}
        </button>
      </div>

      {discrepancies.length > 0 && (
        <div className="discrepancy-alerts mb-lg" data-testid="res-discrepancy-alerts">
          <div className="discrepancy-alerts-header">
            <AlertTriangle size={20} />
            <span>{discrepancies.length} Discrepanc{discrepancies.length > 1 ? "ies" : "y"} Detected</span>
            <span className="badge badge-na">{summ.high_severity || 0} High</span>
            <span className="badge badge-neutral">{summ.medium_severity || 0} Medium</span>
          </div>
          {discrepancies.map((d, i) => (
            <div key={i} className={`discrepancy-alert-item ${d.severity}`}>
              <div className="discrepancy-dept">{d.department}</div>
              <div className="discrepancy-metric">{d.metric}</div>
              <div className="discrepancy-values">
                <span>Inventory: <strong>{d.inventory_value?.toLocaleString()}</strong></span>
                <span>IoT: <strong>{d.iot_value?.toLocaleString()}</strong></span>
              </div>
              <div className={`discrepancy-variance ${d.variance_pct > 0 ? "over" : "under"}`}>
                {d.variance_pct > 0 ? "+" : ""}{d.variance_pct}%
              </div>
            </div>
          ))}
        </div>
      )}

      {discrepancies.length === 0 && (
        <div className="card mb-lg" style={{ borderLeft: "4px solid var(--success)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-sm)" }}>
            <CheckCircle size={20} style={{ color: "var(--success)" }} />
            <span>No significant discrepancies detected. Upload residential inventory and simulate IoT data to enable comparison.</span>
          </div>
        </div>
      )}

      <div className="stats-grid mb-lg">
        <div className="stat-card">
          <div className="stat-icon primary"><GitCompare size={24} /></div>
          <div className="stat-value">{summ.total_departments}</div>
          <div className="stat-label">Categories Compared</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon warning"><AlertTriangle size={24} /></div>
          <div className="stat-value">{summ.total_discrepancies}</div>
          <div className="stat-label">Discrepancies</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon danger"><AlertCircle size={24} /></div>
          <div className="stat-value">{summ.high_severity}</div>
          <div className="stat-label">High Severity</div>
        </div>
      </div>

      <div className="card mb-lg">
        <h4 className="card-title">Inventory vs IoT vs Audit — By Category</h4>
        <div className="table-container">
          <table className="table" data-testid="res-comparison-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Audit %</th>
                <th>Inv. Energy (kWh)</th>
                <th>Inv. Power (kW)</th>
                <th>IoT Energy (kWh)</th>
                <th>IoT Power (kW)</th>
                <th>IoT V (avg)</th>
                <th>Energy Var. %</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {deptData.map((dept, idx) => (
                <tr key={idx} className={dept.variance?.has_discrepancy ? "row-discrepancy" : ""}>
                  <td><strong>{dept.department}</strong></td>
                  <td>{dept.audit?.completion}%</td>
                  <td>{dept.inventory?.energy_kwh?.toLocaleString()}</td>
                  <td>{dept.inventory?.power_kw?.toLocaleString()}</td>
                  <td>{dept.iot?.energy_total_kwh?.toLocaleString()}</td>
                  <td>{dept.iot?.active_power_kw?.toLocaleString()}</td>
                  <td>{dept.iot?.voltage_avg_v || "-"}</td>
                  <td className={`${Math.abs(dept.variance?.energy_pct) > 10 ? "text-danger" : ""}`}>
                    {dept.variance?.energy_pct !== 0 ? `${dept.variance?.energy_pct > 0 ? "+" : ""}${dept.variance?.energy_pct}%` : "-"}
                  </td>
                  <td>
                    {dept.variance?.has_discrepancy ? (
                      <span className="badge badge-na"><AlertTriangle size={12} /> Alert</span>
                    ) : (
                      <span className="badge badge-success"><CheckCircle size={12} /> OK</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bento-grid mb-lg">
        <div className="card">
          <h4 className="card-title">Energy Comparison: Inventory vs IoT (kWh)</h4>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={deptData.map(d => ({
              name: d.department.split(" ")[0],
              inventory: d.inventory?.energy_kwh || 0,
              iot: d.iot?.energy_total_kwh || 0,
              audit: d.audit?.energy_kwh || 0
            }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2EAE5" />
              <XAxis dataKey="name" tick={{ fill: "#526A5A", fontSize: 11 }} />
              <YAxis tick={{ fill: "#526A5A", fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="inventory" fill="#244C36" name="Inventory" radius={[4, 4, 0, 0]} />
              <Bar dataKey="iot" fill="#3B82F6" name="IoT" radius={[4, 4, 0, 0]} />
              <Bar dataKey="audit" fill="#F59E0B" name="Audit" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card">
          <h4 className="card-title">Active Power: Inventory vs IoT (kW)</h4>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={deptData.map(d => ({
              name: d.department.split(" ")[0],
              inventory: d.inventory?.power_kw || 0,
              iot: d.iot?.active_power_kw || 0
            }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2EAE5" />
              <XAxis dataKey="name" tick={{ fill: "#526A5A", fontSize: 11 }} />
              <YAxis tick={{ fill: "#526A5A", fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="inventory" fill="#244C36" name="Inventory (kW)" radius={[4, 4, 0, 0]} />
              <Bar dataKey="iot" fill="#3B82F6" name="IoT (kW)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default ResidentialComparisonView;
